// src/components/TransitMap.js
import React from 'react'
import shareImage from '../utils/shareImage'

// stroke-dasharray value larger than any path in the 200×160 viewBox
const DASH = 500

const MAPS = {
  nyc: {
    label: 'New York',
    lines: [
      { id: '1·2·3',   color: '#EE352E', d: 'M50,12 L50,148' },
      { id: '4·5·6',   color: '#00933C', d: 'M90,12 L90,148' },
      { id: 'A·C·E',   color: '#0039A6', d: 'M12,92 L90,92' },
      { id: 'B·D·F·M', color: '#FF6319', d: 'M12,45 L188,45' },
      { id: 'N·Q·R',   color: '#FCCC0A', d: 'M118,12 L188,82 L188,148' },
      { id: 'L',        color: '#A7A9AC', d: 'M12,125 L188,125' },
      { id: 'G',        color: '#6CBE45', d: 'M130,12 L130,92' },
    ],
    dots: [
      [50,45],[50,92],[50,125],
      [90,45],[90,92],[90,125],
      [130,45],[130,92],[188,82],
    ],
  },
  paris: {
    label: 'Paris',
    lines: [
      { id: '1',     color: '#FFCD00', d: 'M12,80 L188,80' },
      { id: '4',     color: '#662483', d: 'M100,12 L100,148' },
      { id: '2',     color: '#003CA6', d: 'M18,30 L182,30' },
      { id: '6',     color: '#6ECA97', d: 'M18,130 L182,130' },
      { id: '13',    color: '#98D4E2', d: 'M38,12 L78,80 L78,148' },
      { id: 'RER A', color: '#E2231A', d: 'M12,55 L188,55' },
    ],
    dots: [
      [100,80],[78,80],
      [100,30],[100,55],[100,130],
      [78,55],[78,130],[78,30],
    ],
  },
  london: {
    label: 'London',
    lines: [
      { id: 'Central',    color: '#E32017', d: 'M12,80 L188,80' },
      { id: 'Northern',   color: '#898989', d: 'M100,12 L100,148' },
      { id: 'Victoria',   color: '#0098D4', d: 'M58,12 L142,148' },
      { id: 'Jubilee',    color: '#A0A5A9', d: 'M12,48 L100,48 L188,112' },
      { id: 'Piccadilly', color: '#003688', d: 'M12,25 L188,25 L188,118' },
      { id: 'Circle',     color: '#FFD300', d: 'M38,58 L162,58 L162,115 L38,115 Z' },
    ],
    dots: [
      [100,80],[58,80],[142,80],
      [100,48],[100,25],[100,58],[100,115],
      [162,58],[162,80],[162,112],
      [38,58],[38,80],[38,115],
    ],
  },
  milan: {
    label: 'Milan',
    lines: [
      { id: 'M1', color: '#E2001A', d: 'M12,80 L188,80' },
      { id: 'M2', color: '#007A3D', d: 'M30,12 L150,148' },
      { id: 'M3', color: '#F5A800', d: 'M100,12 L100,148' },
      { id: 'M4', color: '#1F7DC2', d: 'M12,118 L188,118' },
      { id: 'M5', color: '#8B1A8B', d: 'M170,12 L50,148' },
    ],
    dots: [
      [100,80],[90,80],[110,80],
      [100,118],[76,118],[124,118],
      [100,92],
    ],
  },
  berlin: {
    label: 'Berlin',
    lines: [
      // S-Bahn Ring: ellipse centered at (100,75), rx=58, ry=42
      { id: 'S-Ring', color: '#6DB33F', d: 'M100,33 A58,42 0 1,1 100,117 A58,42 0 1,1 100,33' },
      { id: 'U2',     color: '#E8472A', d: 'M12,62 L132,62 L188,118' },
      { id: 'U6',     color: '#8C6DAB', d: 'M100,12 L100,148' },
      { id: 'U7',     color: '#007EC6', d: 'M12,110 L188,42' },
      { id: 'U8',     color: '#224F9F', d: 'M68,12 L68,148' },
    ],
    dots: [
      [100,33],[100,117],         // Ring top/bottom (where U6 pierces)
      [68,40],[68,110],           // Ring × U8
      [100,62],[68,62],[132,62],  // U2 intersections
      [100,75],                   // U6/U7 central cross
    ],
  },
  madrid: {
    label: 'Madrid',
    lines: [
      { id: 'L1',  color: '#30BFEF', d: 'M100,12 L100,148' },
      { id: 'L2',  color: '#E52320', d: 'M12,80 L188,80' },
      { id: 'L3',  color: '#FFD700', d: 'M30,18 L170,142' },
      { id: 'L5',  color: '#96BE0E', d: 'M12,48 L188,112' },
      { id: 'L10', color: '#004F9F', d: 'M170,18 L30,142' },
      // Line 6 circular: ellipse centered at (100,80), rx=52, ry=38
      { id: 'L6',  color: '#9E9E9E', d: 'M100,42 A52,38 0 1,1 100,118 A52,38 0 1,1 100,42' },
    ],
    dots: [
      [100,80],                   // central hub
      [100,42],[100,118],         // L6 top/bottom (where L1 pierces)
      [48,80],[152,80],           // L6 × L2
      [75,56],[125,56],           // upper diagonal crossings
      [75,104],[125,104],         // lower diagonal crossings
    ],
  },
}

export const MAPS_LINES = Object.fromEntries(
  Object.entries(MAPS).map(([k, v]) => [k, v.lines])
)

const CITY_KEYS = ['nyc', 'paris', 'london', 'milan', 'berlin', 'madrid']

export function MapSVG({ cityKey, animKey, large }) {
  const map = MAPS[cityKey]
  const [drawing, setDrawing] = React.useState(false)
  const sw  = large ? 3.5 : 2       // stroke width
  const dr  = large ? 5   : 2.5     // dot radius
  const dsw = large ? 1.5 : 1       // dot stroke width

  React.useEffect(() => {
    setDrawing(false)
    const t = setTimeout(() => setDrawing(true), 60)
    return () => clearTimeout(t)
  }, [animKey])

  const dotsStartDelay = (map.lines.length - 1) * 0.18 + 0.9

  return (
    <svg viewBox="0 0 200 160" style={{ width: '100%', height: '100%', display: 'block' }}>
      <style>{`@keyframes mapDotIn{from{opacity:0}to{opacity:1}}`}</style>
      {map.lines.map((line, i) => (
        <path
          key={line.id}
          d={line.d}
          stroke={line.color}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          strokeDasharray={DASH}
          strokeDashoffset={drawing ? 0 : DASH}
          style={{ transition: `stroke-dashoffset 0.9s ease ${i * 0.18}s` }}
        />
      ))}
      {drawing && map.dots.map(([x, y], i) => (
        <circle
          key={i}
          cx={x} cy={y} r={dr}
          fill="#0d1117"
          stroke="#ffffff"
          strokeWidth={dsw}
          style={{
            opacity: 0,
            animation: `mapDotIn 0.25s ease ${dotsStartDelay + i * 0.04}s forwards`,
          }}
        />
      ))}
    </svg>
  )
}

export default function TransitMaps({ stats }) {
  const [fullscreen, setFullscreen] = React.useState(null)
  const [fsKey, setFsKey] = React.useState(0)
  const [sharing, setSharing] = React.useState(false)
  const mapCaptureRef = React.useRef(null)

  const handleOpen = (city) => {
    setFullscreen(city)
    setFsKey(k => k + 1)
  }

  const handleShare = async () => {
    if (!fullscreen || sharing) return
    setSharing(true)
    try {
      const el = mapCaptureRef.current
      if (!el) throw new Error('no ref')
      const h2c = await new Promise(resolve => {
        if (window.html2canvas) { resolve(window.html2canvas); return }
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
        s.onload = () => resolve(window.html2canvas)
        s.onerror = () => resolve(null)
        document.head.appendChild(s)
      })
      if (!h2c) throw new Error('html2canvas unavailable')
      const canvas = await h2c(el, {
        scale: 3, useCORS: true, allowTaint: true,
        backgroundColor: '#0d1117', logging: false,
        width: el.offsetWidth, height: el.offsetHeight,
      })
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.93))
      const stations = stats?.cityStations?.[fullscreen] || 0
      const cityLabel = MAPS[fullscreen].label
      const text = stations >= 28
        ? `I completed the ${cityLabel} line on The Magic Journey ✦ magic.soappower.com`
        : `I'm exploring ${cityLabel} on The Magic Journey — 28 days of gratitude ✦ magic.soappower.com`
      await shareImage(blob, `magic-${fullscreen}-map.jpg`, text)
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Share error:', e)
    }
    setSharing(false)
  }

  return (
    <>
      <div style={{ padding: '20px 16px 8px' }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: 'var(--text4)', fontWeight: 700, marginBottom: 12 }}>
          TRANSIT MAPS
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {CITY_KEYS.map(c => {
            const unlocked = (stats?.cityStations?.[c] || 0) >= 1
            const map = MAPS[c]
            return (
              <div
                key={c}
                onClick={() => unlocked && handleOpen(c)}
                style={{
                  position: 'relative',
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: '#0d1117',
                  border: `1px solid ${unlocked ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)'}`,
                  aspectRatio: '5 / 4',
                  cursor: unlocked ? 'pointer' : 'default',
                  opacity: unlocked ? 1 : 0.4,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <MapSVG cityKey={c} animKey={c} />

                {/* city label */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '6px 8px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)',
                  fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
                  color: unlocked ? '#fff' : 'rgba(255,255,255,0.35)',
                }}>
                  {map.label.toUpperCase()}
                </div>

                {/* lock overlay */}
                {!unlocked && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}>
                    <svg width="14" height="17" viewBox="0 0 14 17" fill="none">
                      <rect x="1" y="7" width="12" height="9" rx="2" fill="rgba(255,255,255,0.25)" />
                      <path d="M3.5 7V5a3.5 3.5 0 0 1 7 0v2" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" fill="none" />
                    </svg>
                    <span style={{ fontSize: 8, letterSpacing: 1.5, color: 'rgba(255,255,255,0.3)', fontWeight: 700 }}>
                      DAY 1
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* fullscreen viewer */}
      {fullscreen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: '#0d1117',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* header */}
          <div style={{ padding: '52px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: 3, color: 'rgba(255,255,255,0.35)', fontWeight: 700, marginBottom: 4 }}>
                TRANSIT MAP
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: -0.5 }}>
                {MAPS[fullscreen].label}
              </div>
            </div>
            <button
              onClick={() => setFullscreen(null)}
              style={{
                background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: 16, flexShrink: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >✕</button>
          </div>

          {/* map */}
          <div ref={mapCaptureRef} style={{ flex: 1, padding: '8px 28px', display: 'flex', alignItems: 'center', background: '#0d1117' }}>
            <div style={{ width: '100%' }}>
              <MapSVG cityKey={fullscreen} animKey={fsKey} large />
            </div>
          </div>

          {/* line legend */}
          <div style={{ padding: '0 28px 16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
              {MAPS[fullscreen].lines.map(line => (
                <div key={line.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 18, height: 3, borderRadius: 2, background: line.color }} />
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: 1, fontWeight: 600 }}>
                    {line.id}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* share button */}
          <div style={{ padding: '0 28px 44px' }}>
            <button
              onClick={handleShare}
              disabled={sharing}
              style={{
                width: '100%', background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12,
                padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                cursor: sharing ? 'default' : 'pointer', fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent', opacity: sharing ? 0.5 : 1,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>
                {sharing ? 'SHARING…' : 'SHARE MAP'}
              </span>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
